import React, { useState, useCallback, useMemo } from 'react';
import { uploadFile } from '@services/File';
import { useEditLesson } from '../../useEditLesson';
import { useSllUpdateLessonMutation, SllLessonForSaFragmentDoc } from '@api/graphql-codegen';
import EditLessonForm, { EditSllLesson } from './EditLesson';
import { gradeLabelPositionOptions } from '@constants';
import { useToast } from '@src/hooks/useToast';
import { useAWSKeys } from '@src/hooks';
import { logger } from '../../../../../utils/logger';

type Props = {
  refetchLesson?: () => Promise<any>
};

function EditLessonContent(props: Props) {
  const { refetchLesson } = props
  const [loading, setLoading] = useState(false);
  const { lesson } = useEditLesson();
  const { successToast, errorToast } = useToast();

  const { awsKeys } = useAWSKeys();

  const [sllUpdateLesson] = useSllUpdateLessonMutation({
    update(cache, res) {
      const d = res?.data?.sllUpdateLesson;
      if (d) {
        cache.writeFragment({
          id: cache.identify(d),
          data: { ...d },
          fragment: SllLessonForSaFragmentDoc,
          fragmentName: 'SLLLessonForSA'
        });
      }
    },
    onError(e) {
      logger.debug(`[e]`, e);
      errorToast('Lesson update failed. Please try again!')
    },
    onCompleted(data) {
      if (data?.sllUpdateLesson) {
        successToast('Lesson is updated successfully!')
      }
    }
  });

  const handleEdit = useCallback(async ({ bannerImage, coverImage, __typename, id, recommendedPrior, recommendedNext, ...lessonObj }: EditSllLesson) => {
    if (!lesson?.id) return;
    try {
      setLoading(true);
      const updatedLesson: EditSllLesson = lessonObj;
      if (coverImage && typeof coverImage !== 'string') {
        updatedLesson['coverImage'] = await uploadFile(awsKeys, coverImage, 'images');
      } else {
        updatedLesson['coverImage'] = coverImage;
      }
      if (bannerImage && typeof bannerImage !== 'string') {
        updatedLesson['bannerImage'] = await uploadFile(awsKeys, bannerImage, 'images');
      } else {
        updatedLesson['bannerImage'] = bannerImage;
      }
      const priorLessons = JSON.parse(recommendedPrior || '[]');
      const nextLessons = JSON.parse(recommendedNext || '[]');
      try {
        await sllUpdateLesson({
          variables: {
            input: {
              ...updatedLesson,
              id: lesson?.id,
              gradeLabelPosition: updatedLesson.gradeLabelPosition || gradeLabelPositionOptions[2].value,
              comingSoonLabelPosition: updatedLesson.comingSoonLabelPosition || gradeLabelPositionOptions[3].value,
              coverImage: updatedLesson['coverImage'],
              bannerImage: updatedLesson['bannerImage'],
              recommendedPrior: priorLessons,
              recommendedNext: nextLessons,
            },
          }
        });
      } catch (e) {
        logger.debug(`[e]--`, e);
      }
      if (refetchLesson) {
        await refetchLesson();
      }
      setLoading(false);
    } catch (e) {
      logger.debug(`[e]`, e);
      setLoading(false);
    }
  }, [lesson, refetchLesson, awsKeys, sllUpdateLesson]);

  const [lessonContent] = useMemo(() => {
    if (lesson) {
      const { materials, presentation, meta, sections, ...lessonContentData } = lesson;
      return [lessonContentData]
    }
    return [undefined]
  }, [lesson]);

  return (
    <EditLessonForm loading={loading} onSave={handleEdit} sllLesson={lessonContent} setLoading={setLoading} />
  );
}

export default EditLessonContent;
